import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  FolderKanban,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { UniversalOperationalRecordDrawer } from "../../components/dashboard/UniversalOperationalRecordDrawer";
import { BusinessCenterOSAdminShell } from "../../components/layout/BusinessCenterOSAdminShell";
import { getAdminClients, getBusinessCenterProjects } from "../../lib/api";
import { formatDate } from "../../lib/format";
import type { BusinessCenterProjectRecord, ClientRecord, BusinessCenterTaskRecord } from "../../lib/types";
import {
  appendARRecoveryActivityNote,
  appendUniversalActivity,
  appendUniversalAuditEntry,
  createARRecoveryActivityNote,
  createUniversalActivityEvent,
  createUniversalAuditTrailEntry,
  createUniversalBusinessNote,
  loadUniversalOperationalFoundationState,
  updateUniversalTicklerStatus,
  upsertUniversalBusinessNote,
  upsertUniversalTickler,
  type UniversalBusinessNote,
  type UniversalOperationalFoundationState,
  type UniversalRecordType,
  type UniversalTickler,
  type UniversalWorkspaceKey,
} from "../../lib/universalOperationalEngines";
import {
  getMyOpenTicklers,
  getMyOverdueTicklers,
  getTicklersDueToday,
  loadOperationalState,
  saveOperationalState,
} from "../../lib/universalOperationalPersistence";
import {
  createBusinessCenterTask,
  loadBusinessCenterTasksForBrand,
  refreshBusinessCenterTasks,
  taskPriorityOptions,
  taskStatusOptions,
  upsertBusinessCenterTask,
  updateBusinessCenterTask,
  removeBusinessCenterTask,
} from "./businessCenterTasksData";

type BrandValue = "kreatethedate" | "truevance_connect";
type ViewMode = "list" | "board";
type QueueView = "due-today" | "overdue" | "open" | "escalated" | "completed" | "all";
type QueueDueFilter = "all" | "today" | "overdue" | "future" | "no-date";

const queueViews: Array<{ key: QueueView; label: string }> = [
  { key: "due-today", label: "Due Today" },
  { key: "overdue", label: "Overdue" },
  { key: "open", label: "Open" },
  { key: "escalated", label: "Escalated" },
  { key: "completed", label: "Completed" },
  { key: "all", label: "All" },
];

const workspaceOptions: Array<{ value: UniversalWorkspaceKey | "all"; label: string }> = [
  { value: "all", label: "All Workspaces" },
  { value: "business-center-os", label: "Business Center OS" },
  { value: "ar-recovery-os", label: "CollabRecover AR Solutions" },
  { value: "truevance-connect-os", label: "Truevance Connect OS" },
  { value: "kreatethedate-os", label: "Kreatethedate OS" },
  { value: "memory-media-vault-os", label: "Memory Media Vault OS" },
  { value: "future-template", label: "Future Templates" },
];

const brandOptions: Array<{ value: BrandValue; label: string; accent: string }> = [
  { value: "truevance_connect", label: "Truevance Connect", accent: "#06B6D4" },
  { value: "kreatethedate", label: "Kreatethedate", accent: "#FF6B35" },
];

interface TaskFormState {
  brand: BrandValue;
  taskTitle: string;
  description: string;
  dueDate: string;
  status: BusinessCenterTaskRecord["status"];
  priority: BusinessCenterTaskRecord["priority"];
  clientId: string;
  projectId: string;
  assignedMember: string;
  notes: string;
}

const blankForm: TaskFormState = {
  brand: "truevance_connect",
  taskTitle: "",
  description: "",
  dueDate: "",
  status: "Not Started",
  priority: "Medium",
  clientId: "",
  projectId: "",
  assignedMember: "Team Member Placeholder",
  notes: "",
};

export function BusinessCenterOSTasksPage() {
  const [selectedBrand, setSelectedBrand] = useState<BrandValue>("truevance_connect");
  const [tasks, setTasks] = useState<BusinessCenterTaskRecord[]>([]);
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [projects, setProjects] = useState<BusinessCenterProjectRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingWorkspace, setLoadingWorkspace] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [showCreateForm, setShowCreateForm] = useState(true);
  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [form, setForm] = useState<TaskFormState>(blankForm);
  const [operationalState, setOperationalState] = useState<UniversalOperationalFoundationState>(() => loadUniversalOperationalFoundationState());
  const [operationalStatus, setOperationalStatus] = useState("Operational records use local fallback until backend sync completes.");
  const [queueView, setQueueView] = useState<QueueView>("due-today");
  const [assignedUserFilter, setAssignedUserFilter] = useState("all");
  const [workspaceFilter, setWorkspaceFilter] = useState<UniversalWorkspaceKey | "all">("all");
  const [statusFilter, setStatusFilter] = useState<UniversalTickler["status"] | "all">("all");
  const [priorityFilter, setPriorityFilter] = useState<UniversalTickler["priority"] | "all">("all");
  const [dueDateFilter, setDueDateFilter] = useState<QueueDueFilter>("all");
  const [recordTypeFilter, setRecordTypeFilter] = useState<UniversalRecordType | "all">("all");
  const [reminderTypeFilter, setReminderTypeFilter] = useState<UniversalTickler["reminderType"] | "all">("all");
  const [selectedTicklerId, setSelectedTicklerId] = useState("");
  const [recordDrawerOpen, setRecordDrawerOpen] = useState(false);
  const [activityNote, setActivityNote] = useState("");

  useBusinessCenterOSBranding("Universal Work Queue", "View and manage ticklers, reminders, follow-ups, overdue items, and operational tasks across every workspace.");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadingWorkspace(true);
    setError("");
    refreshBusinessCenterTasks()
      .then(() => {
        if (!cancelled) setTasks(loadBusinessCenterTasksForBrand(selectedBrand));
      })
      .catch(() => {
        if (!cancelled) setTasks(loadBusinessCenterTasksForBrand(selectedBrand));
      });

    void Promise.all([getAdminClients({ brand: selectedBrand }), getBusinessCenterProjects({ brand: selectedBrand })])
      .then(([brandClients, brandProjects]) => {
        if (cancelled) return;
        setClients(brandClients);
        setProjects(brandProjects.projects || []);
      })
      .catch((loadError) => {
        if (!cancelled) {
          setClients([]);
          setProjects([]);
          setError(
            loadError instanceof Error && loadError.message.includes("Failed")
              ? loadError.message
              : "Task workspace is using local fallback data because backend client/project lookup is unavailable.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setLoadingWorkspace(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedBrand]);

  useEffect(() => {
    let cancelled = false;
    void loadOperationalState().then((result) => {
      if (cancelled) return;
      setOperationalState(result.state);
      setOperationalStatus(
        result.backendAvailable
          ? "Operational records are synced through the backend state store with local fallback."
          : "Backend sync is unavailable; operational records are saved locally in this browser.",
      );
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredTasks = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return tasks.filter((task) =>
      [
        task.taskNumber,
        task.taskTitle,
        task.description,
        task.clientName,
        task.projectName,
        task.status,
        task.priority,
        task.assignedMember,
        task.notes,
      ]
        .join(" ")
        .toLowerCase()
        .includes(normalizedQuery),
    );
  }, [tasks, query]);

  const metrics = useMemo(() => {
    const counts = taskStatusOptions.reduce<Record<string, number>>((acc, status) => {
      acc[status] = filteredTasks.filter((task) => task.status === status).length;
      return acc;
    }, {});
    return [
      { label: "Tasks", value: filteredTasks.length, note: "Brand-scoped task records in the current workspace." },
      { label: "Not Started", value: counts["Not Started"] || 0, note: "Tasks waiting for action." },
      { label: "In Progress", value: counts["In Progress"] || 0, note: "Tasks actively underway." },
      { label: "Waiting", value: counts.Waiting || 0, note: "Tasks blocked on input or approval." },
      { label: "Complete", value: counts.Complete || 0, note: "Tasks already finished." },
      { label: "On Hold", value: counts["On Hold"] || 0, note: "Tasks paused for later." },
    ];
  }, [filteredTasks]);
  const selectedTask = tasks.find((task) => task.id === selectedTaskId) || filteredTasks[0] || tasks[0] || null;
  const today = new Date().toISOString().slice(0, 10);
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString();
  const queueTicklers = operationalState.ticklers;
  const dueTodayTicklers = getTicklersDueToday(operationalState);
  const overdueTicklers = queueTicklers.filter((tickler) => tickler.status !== "Completed" && (tickler.status === "Overdue" || tickler.dueDate < today));
  const openTicklers = queueTicklers.filter((tickler) => tickler.status === "Open");
  const escalatedTicklers = queueTicklers.filter((tickler) => tickler.status === "Escalated");
  const completedThisWeekTicklers = queueTicklers.filter((tickler) => tickler.status === "Completed" && tickler.updatedAt >= weekAgo);
  const assignedQueueLabel = assignedUserFilter === "all" ? "All users" : assignedUserFilter;
  const assignedOpenCount = assignedUserFilter === "all" ? openTicklers.length : getMyOpenTicklers(operationalState, assignedUserFilter).length;
  const assignedOverdueCount = assignedUserFilter === "all" ? overdueTicklers.length : getMyOverdueTicklers(operationalState, assignedUserFilter).length;
  const universalStatusStrip = [
    { label: "Due today", value: dueTodayTicklers.length },
    { label: "Overdue", value: overdueTicklers.length },
    { label: "Open", value: openTicklers.length },
    { label: "Escalated", value: escalatedTicklers.length },
    { label: "Completed this week", value: completedThisWeekTicklers.length },
  ];
  const assignedUserOptions = useMemo(
    () => Array.from(new Set(queueTicklers.map((tickler) => tickler.assignedUser).filter(Boolean))).sort(),
    [queueTicklers],
  );
  const recordTypeOptions = useMemo(
    () => Array.from(new Set(queueTicklers.map((tickler) => tickler.recordType))).sort(),
    [queueTicklers],
  );
  const reminderTypeOptions = useMemo(
    () => Array.from(new Set(queueTicklers.map((tickler) => tickler.reminderType))).sort(),
    [queueTicklers],
  );
  const filteredQueueItems = useMemo(() => {
    return queueTicklers
      .filter((tickler) => {
        if (queueView === "due-today" && !(tickler.dueDate === today && tickler.status !== "Completed")) return false;
        if (queueView === "overdue" && !(tickler.status !== "Completed" && (tickler.status === "Overdue" || tickler.dueDate < today))) return false;
        if (queueView === "open" && tickler.status !== "Open") return false;
        if (queueView === "escalated" && tickler.status !== "Escalated") return false;
        if (queueView === "completed" && tickler.status !== "Completed") return false;
        if (assignedUserFilter !== "all" && tickler.assignedUser !== assignedUserFilter) return false;
        if (workspaceFilter !== "all" && tickler.workspace !== workspaceFilter) return false;
        if (statusFilter !== "all" && tickler.status !== statusFilter) return false;
        if (priorityFilter !== "all" && tickler.priority !== priorityFilter) return false;
        if (recordTypeFilter !== "all" && tickler.recordType !== recordTypeFilter) return false;
        if (reminderTypeFilter !== "all" && tickler.reminderType !== reminderTypeFilter) return false;
        if (dueDateFilter === "today" && tickler.dueDate !== today) return false;
        if (dueDateFilter === "overdue" && !(tickler.status !== "Completed" && tickler.dueDate < today)) return false;
        if (dueDateFilter === "future" && tickler.dueDate <= today) return false;
        if (dueDateFilter === "no-date" && tickler.dueDate) return false;
        return true;
      })
      .sort((a, b) => `${a.dueDate} ${a.dueTime}`.localeCompare(`${b.dueDate} ${b.dueTime}`));
  }, [assignedUserFilter, dueDateFilter, priorityFilter, queueTicklers, queueView, recordTypeFilter, reminderTypeFilter, statusFilter, today, workspaceFilter]);
  const selectedTickler = queueTicklers.find((tickler) => tickler.id === selectedTicklerId) || filteredQueueItems[0] || null;
  const selectedActivity = selectedTickler
    ? operationalState.activityEvents.filter((event) => event.recordId === selectedTickler.recordId && event.workspace === selectedTickler.workspace).slice(0, 5)
    : [];

  useEffect(() => {
    if (!selectedTaskId && selectedTask) {
      setSelectedTaskId(selectedTask.id);
    }
  }, [selectedTask, selectedTaskId]);

  useEffect(() => {
    if (!selectedTicklerId && selectedTickler) setSelectedTicklerId(selectedTickler.id);
  }, [selectedTickler, selectedTicklerId]);

  function updateForm<K extends keyof TaskFormState>(key: K, value: TaskFormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function resetForm(nextBrand: BrandValue = selectedBrand) {
    setForm({ ...blankForm, brand: nextBrand });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const client = clients.find((item) => item.id === form.clientId);
      const project = projects.find((item) => item.id === form.projectId);
      const nextTask = createBusinessCenterTask({
        brand: form.brand,
        clientId: client?.id || null,
        clientName: client?.clientName || "Unlinked Client",
        projectId: project?.id || null,
        projectName: project?.projectName || "Unlinked Project",
        taskTitle: form.taskTitle,
        description: form.description,
        dueDate: form.dueDate || null,
        status: form.status,
        priority: form.priority,
        assignedMember: form.assignedMember,
        notes: form.notes,
      });
      const persisted = upsertBusinessCenterTask(nextTask);
      setTasks(persisted.filter((task) => task.brand === selectedBrand));
      setSelectedTaskId(nextTask.id);
      resetForm(form.brand);
      setShowCreateForm(false);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Unable to create task.");
    } finally {
      setSaving(false);
    }
  }

  function handleTaskStatusChange(taskId: string, nextStatus: BusinessCenterTaskRecord["status"]) {
    const nextTasks = updateBusinessCenterTask(taskId, { status: nextStatus });
    setTasks(nextTasks.filter((task) => task.brand === selectedBrand));
  }

  function handleDeleteTask(taskId: string) {
    const nextTasks = removeBusinessCenterTask(taskId);
    setTasks(nextTasks.filter((task) => task.brand === selectedBrand));
    setSelectedTaskId(nextTasks.find((task) => task.brand === selectedBrand)?.id || "");
  }

  function updateOperationalQueue(mutator: (current: UniversalOperationalFoundationState) => UniversalOperationalFoundationState) {
    setOperationalState((current) => {
      const next = mutator(current);
      void saveOperationalState(next).then((result) => {
        setOperationalStatus(
          result.backendAvailable
            ? "Operational records are synced through the backend state store with local fallback."
            : "Backend sync is unavailable; operational records are saved locally in this browser.",
        );
      });
      return next;
    });
  }

  function handleUniversalTicklerStatus(tickler: UniversalTickler, status: UniversalTickler["status"]) {
    const result = updateUniversalTicklerStatus(tickler, status, tickler.assignedUser);
    updateOperationalQueue((current) =>
      appendUniversalAuditEntry(appendUniversalActivity(upsertUniversalTickler(current, result.tickler), result.activityEvent), result.auditEntry),
    );
    setSelectedTicklerId(tickler.id);
  }

  function createRecordDetailActivity(tickler: UniversalTickler, description: string, action: string, fieldChanged: string) {
    const timestamp = new Date().toISOString();
    const event = createUniversalActivityEvent({
      workspace: tickler.workspace,
      recordType: tickler.recordType,
      recordId: tickler.recordId,
      user: tickler.assignedUser,
      role: "operator",
      timestamp,
      action,
      description,
      outcome: tickler.status,
      visibility: "internal",
    });
    const audit = createUniversalAuditTrailEntry({
      user: tickler.assignedUser,
      timestamp,
      recordType: tickler.recordType,
      recordId: tickler.recordId,
      fieldChanged,
      previousValue: "None",
      newValue: description,
      workspace: tickler.workspace,
      visibility: "internal",
    });
    return { audit, event };
  }

  function handleRecordDetailActivity(tickler: UniversalTickler, note: string) {
    const description = note.trim();
    if (!description) return;
    const { audit, event } = createRecordDetailActivity(tickler, description, "Record detail activity added", "record_detail.activity");
    updateOperationalQueue((current) => appendUniversalAuditEntry(appendUniversalActivity(current, event), audit));
    setSelectedTicklerId(tickler.id);
  }

  function handleRecordDetailNote(tickler: UniversalTickler, note: string) {
    const description = note.trim();
    if (!description) return;
    const { audit, event } = createRecordDetailActivity(tickler, description, "Record detail note added", "record_detail.note");
    updateOperationalQueue((current) => {
      const withActivity = appendUniversalAuditEntry(appendUniversalActivity(current, event), audit);
      if (tickler.workspace === "ar-recovery-os" && (tickler.recordType === "ar_account" || tickler.recordType === "recovery_record")) {
        return appendARRecoveryActivityNote(
          withActivity,
          createARRecoveryActivityNote({
            noteType: "Recovery Account",
            collector: tickler.assignedUser,
            timestamp: event.timestamp,
            accountId: tickler.recordId,
            accountName: tickler.recordId,
            outcome: tickler.status,
            nextAction: tickler.notes,
            content: description,
            visibility: "internal",
          }),
        );
      }
      if (isUniversalBusinessNoteRecordType(tickler.recordType)) {
        return upsertUniversalBusinessNote(
          withActivity,
          createUniversalBusinessNote({
            author: tickler.assignedUser,
            timestamp: event.timestamp,
            workspace: tickler.workspace,
            recordType: tickler.recordType,
            recordId: tickler.recordId,
            noteCategory: "General",
            visibility: "internal",
            content: description,
          }),
        );
      }
      return withActivity;
    });
    setSelectedTicklerId(tickler.id);
  }

  function handleAddActivityNote() {
    if (!selectedTickler || !activityNote.trim()) return;
    handleRecordDetailNote(selectedTickler, activityNote);
    setActivityNote("");
  }

  const kanbanColumns = taskStatusOptions.map((status) => ({
    status,
    tasks: filteredTasks.filter((task) => task.status === status),
  }));

  return (
    <BusinessCenterOSAdminShell
      title="Universal Work Queue"
      description="View and manage ticklers, reminders, follow-ups, overdue items, and operational tasks across every workspace."
      statusItems={universalStatusStrip}
    >
      <section className="min-w-0 overflow-x-hidden py-2">
        <div className="mx-auto grid min-w-0 max-w-7xl gap-8">
          <section className="grid min-w-0 gap-6">
            <div className="business-center-os-card business-center-os-card--dark min-w-0 overflow-hidden rounded-3xl p-5 sm:p-6">
              <div className="flex flex-col gap-4 border-b border-[rgba(6,182,212,.16)] pb-5 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Daily Operations</p>
                  <h2 className="mt-1 font-display text-3xl font-bold">Universal queue</h2>
                  <p className="mt-2 max-w-3xl text-sm font-semibold leading-7 text-[#E2E8F0]">
                    See what is due today, overdue, assigned, escalated, and ready for follow-up across Business Center OS, CollabRecover AR Solutions, Truevance Connect, Kreatethedate, Memory Media Vault, and future workspace templates.
                  </p>
                </div>
                <div className="rounded-2xl border border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.04)] px-4 py-3 text-xs font-bold uppercase tracking-[0.12em] text-[#CBD5E1]">
                  {operationalStatus}
                </div>
              </div>

              <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <QueueSelect label="Assigned user" value={assignedUserFilter} onChange={setAssignedUserFilter} options={[{ value: "all", label: "All users" }, ...assignedUserOptions.map((user) => ({ value: user, label: user }))]} />
                <QueueSelect label="Workspace" value={workspaceFilter} onChange={(value) => setWorkspaceFilter(value as UniversalWorkspaceKey | "all")} options={workspaceOptions} />
                <QueueSelect label="Status" value={statusFilter} onChange={(value) => setStatusFilter(value as UniversalTickler["status"] | "all")} options={[{ value: "all", label: "All statuses" }, "Open", "Completed", "Snoozed", "Overdue", "Escalated"].map((status) => (typeof status === "string" ? { value: status, label: status } : status))} />
                <QueueSelect label="Priority" value={priorityFilter} onChange={(value) => setPriorityFilter(value as UniversalTickler["priority"] | "all")} options={[{ value: "all", label: "All priorities" }, "Critical", "High", "Medium", "Low"].map((priority) => (typeof priority === "string" ? { value: priority, label: priority } : priority))} />
                <QueueSelect label="Due date" value={dueDateFilter} onChange={(value) => setDueDateFilter(value as QueueDueFilter)} options={[{ value: "all", label: "All due dates" }, { value: "today", label: "Due today" }, { value: "overdue", label: "Overdue" }, { value: "future", label: "Future" }, { value: "no-date", label: "No date" }]} />
                <QueueSelect label="Record type" value={recordTypeFilter} onChange={(value) => setRecordTypeFilter(value as UniversalRecordType | "all")} options={[{ value: "all", label: "All record types" }, ...recordTypeOptions.map((type) => ({ value: type, label: formatQueueLabel(type) }))]} />
                <QueueSelect label="Reminder type" value={reminderTypeFilter} onChange={(value) => setReminderTypeFilter(value as UniversalTickler["reminderType"] | "all")} options={[{ value: "all", label: "All reminder types" }, ...reminderTypeOptions.map((type) => ({ value: type, label: type }))]} />
                <div className="rounded-2xl border border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.04)] p-4">
                  <p className="text-xs font-black uppercase tracking-[0.14em] text-[#06B6D4]">{assignedQueueLabel}</p>
                  <p className="mt-2 text-sm font-semibold leading-6 text-[#E2E8F0]">{assignedOpenCount} open / {assignedOverdueCount} overdue</p>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-2" role="tablist" aria-label="Universal queue views">
                {queueViews.map((view) => (
                  <button
                    key={view.key}
                    type="button"
                    onClick={() => setQueueView(view.key)}
                    className={`business-center-os-button focus-ring inline-flex min-h-11 items-center rounded-full px-4 text-sm font-extrabold ${queueView === view.key ? "business-center-os-button--primary" : "business-center-os-button--secondary"}`}
                    aria-pressed={queueView === view.key}
                  >
                    {view.label}
                  </button>
                ))}
              </div>

              <div className="mt-6 grid min-w-0 gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
                <div className="grid min-w-0 gap-3">
                  {filteredQueueItems.length ? (
                    filteredQueueItems.map((tickler) => (
                      <UniversalQueueCard
                        key={tickler.id}
                        tickler={tickler}
                        selected={selectedTickler?.id === tickler.id}
                        onSelect={() => setSelectedTicklerId(tickler.id)}
                        onOpenDrawer={() => {
                          setSelectedTicklerId(tickler.id);
                          setRecordDrawerOpen(true);
                        }}
                        onStatusChange={(status) => handleUniversalTicklerStatus(tickler, status)}
                      />
                    ))
                  ) : (
                    <UniversalQueueEmptyState queueView={queueView} assignedUser={assignedUserFilter} />
                  )}
                </div>

                <aside className="min-w-0 rounded-3xl border border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.04)] p-5">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Selected Item</p>
                  {selectedTickler ? (
                    <div className="mt-4 grid gap-4">
                      <h3 className="font-display text-2xl font-bold text-[#F8FAFC]">{selectedTickler.reminderType}</h3>
                      <InfoLine label="Workspace" value={formatQueueLabel(selectedTickler.workspace)} />
                      <InfoLine label="Related record" value={`${formatQueueLabel(selectedTickler.recordType)} / ${selectedTickler.recordId}`} />
                      <InfoLine label="Assigned user" value={selectedTickler.assignedUser} />
                      <InfoLine label="Due" value={`${formatDate(selectedTickler.dueDate)} at ${selectedTickler.dueTime}`} />
                      <InfoLine label="Next action" value={selectedTickler.notes} />
                      <label className="grid gap-2 text-sm font-extrabold text-[#F8FAFC]">
                        Add activity note
                        <textarea value={activityNote} onChange={(event) => setActivityNote(event.target.value)} rows={4} className="business-center-os-field business-center-os-field--dark focus-ring rounded-2xl px-4 py-3 text-sm font-semibold" />
                      </label>
                      <button type="button" disabled={!activityNote.trim()} onClick={handleAddActivityNote} className="business-center-os-button business-center-os-button--primary focus-ring inline-flex min-h-11 items-center justify-center rounded-full px-4 text-sm font-extrabold disabled:opacity-60">
                        Add Note
                      </button>
                      <div className="grid gap-2">
                        <p className="text-xs font-black uppercase tracking-[0.14em] text-[#06B6D4]">Recent activity</p>
                        {selectedActivity.length ? (
                          selectedActivity.map((event) => (
                            <div key={event.id} className="rounded-2xl border border-[rgba(6,182,212,.12)] bg-[rgba(7,26,47,.7)] p-3">
                              <p className="text-sm font-black text-[#F8FAFC]">{event.action}</p>
                              <p className="mt-1 text-xs font-semibold leading-5 text-[#CBD5E1]">{formatDate(event.timestamp)} / {event.outcome}</p>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm font-semibold leading-7 text-[#CBD5E1]">No activity has been recorded for this related record yet.</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="mt-4 text-sm font-semibold leading-7 text-[#CBD5E1]">Select a queue item to inspect activity and add a note.</p>
                  )}
                </aside>
              </div>
            </div>
          </section>

          <section className="grid min-w-0 gap-3">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#14B8A6]">Brand Task Module</p>
              <h2 className="mt-1 font-display text-3xl font-bold text-[#F8FAFC]">Existing task records</h2>
              <p className="mt-2 max-w-3xl text-sm font-semibold leading-7 text-[#CBD5E1]">
                The original brand-scoped task center remains available below the universal operational queue.
              </p>
            </div>
          </section>

          <section className="grid min-w-0 gap-4 sm:grid-cols-2 xl:grid-cols-6">
            {metrics.map((metric) => (
              <article key={metric.label} className="business-center-os-card business-center-os-card--light rounded-3xl p-5">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#14B8A6]">{metric.label}</p>
                <p className="mt-3 font-display text-4xl font-bold text-[#1F2937]">{metric.value}</p>
                <p className="mt-2 text-sm font-semibold leading-6 text-[#475569]">{metric.note}</p>
              </article>
            ))}
          </section>

          {error ? (
            <section className="business-center-os-card business-center-os-card--light min-w-0 rounded-3xl border border-[rgba(255,107,53,.2)] p-4 text-[#1F2937]">
              <p className="text-sm font-bold">Tasks module notice</p>
              <p className="mt-2 text-sm font-semibold text-[#475569]">{error}</p>
            </section>
          ) : null}

            <section className="grid min-w-0 gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,.92fr)]">
              <article className="business-center-os-card business-center-os-card--dark min-w-0 overflow-hidden rounded-3xl p-5 sm:p-6">
              <div className="flex flex-col gap-4 border-b border-[rgba(6,182,212,.16)] pb-5 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Task Workspace</p>
                  <h2 className="mt-1 font-display text-3xl font-bold">Task records</h2>
                  <p className="mt-2 max-w-3xl text-sm font-semibold leading-7 text-[#E2E8F0]">
                    Use this workspace to manage business actions across clients and projects without changing the
                    Memory Media Vault experience.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setViewMode("list")}
                    className={`business-center-os-button focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold ${
                      viewMode === "list" ? "business-center-os-button--primary" : "business-center-os-button--secondary"
                    }`}
                  >
                    <CheckCircle2 className="size-4" aria-hidden="true" />
                    List View
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode("board")}
                    className={`business-center-os-button focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold ${
                      viewMode === "board" ? "business-center-os-button--primary" : "business-center-os-button--secondary"
                    }`}
                  >
                    <FolderKanban className="size-4" aria-hidden="true" />
                    Kanban View
                  </button>
                </div>
              </div>

              <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_320px]">
                <label className="relative block">
                  <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#06B6D4]" aria-hidden="true" />
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 w-full rounded-2xl pl-11 pr-4 text-sm font-semibold"
                    placeholder="Search task title, client, project, status, priority, or notes"
                  />
                </label>

                <div className="grid gap-2">
                  <label className="grid gap-2 text-sm font-extrabold text-[#F8FAFC]">
                    Brand Scope
                    <select
                      value={selectedBrand}
                      onChange={(event) => {
                        const nextBrand = event.target.value as BrandValue;
                        setSelectedBrand(nextBrand);
                        setForm((current) => ({ ...current, brand: nextBrand }));
                      }}
                      className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold"
                    >
                      {brandOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowCreateForm((current) => !current)}
                    className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-full px-4 text-sm font-extrabold"
                  >
                    <Plus className="size-4" aria-hidden="true" />
                    {showCreateForm ? "Hide Create Form" : "New Task"}
                  </button>
                </div>
              </div>

              {showCreateForm ? (
                <form onSubmit={handleSubmit} className="mt-5 grid gap-4 rounded-3xl border border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.04)] p-4 sm:p-5">
                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Task title" dark>
                      <input value={form.taskTitle} onChange={(event) => updateForm("taskTitle", event.target.value)} className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-semibold" />
                    </Field>
                    <Field label="Due date" dark>
                      <input type="date" value={form.dueDate} onChange={(event) => updateForm("dueDate", event.target.value)} className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-semibold" />
                    </Field>
                    <Field label="Status" dark>
                      <select value={form.status} onChange={(event) => updateForm("status", event.target.value as TaskFormState["status"])} className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold">
                        {taskStatusOptions.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Priority" dark>
                      <select value={form.priority} onChange={(event) => updateForm("priority", event.target.value as TaskFormState["priority"])} className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold">
                        {taskPriorityOptions.map((priority) => (
                          <option key={priority} value={priority}>
                            {priority}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Client link" dark>
                      <select value={form.clientId} onChange={(event) => updateForm("clientId", event.target.value)} className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold">
                        <option value="">No client linked</option>
                        {clients.map((client) => (
                          <option key={client.id} value={client.id}>
                            {client.clientName}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Project link" dark>
                      <select value={form.projectId} onChange={(event) => updateForm("projectId", event.target.value)} className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold">
                        <option value="">No project linked</option>
                        {projects.map((project) => (
                          <option key={project.id} value={project.id}>
                            {project.projectName}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Assigned member placeholder" dark>
                      <input value={form.assignedMember} onChange={(event) => updateForm("assignedMember", event.target.value)} className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-semibold" placeholder="Team Member Placeholder" />
                    </Field>
                  </div>
                  <Field label="Description" dark>
                    <textarea rows={4} value={form.description} onChange={(event) => updateForm("description", event.target.value)} className="business-center-os-field business-center-os-field--dark focus-ring rounded-2xl px-4 py-3 text-sm font-semibold" />
                  </Field>
                  <Field label="Notes" dark>
                    <textarea rows={4} value={form.notes} onChange={(event) => updateForm("notes", event.target.value)} className="business-center-os-field business-center-os-field--dark focus-ring rounded-2xl px-4 py-3 text-sm font-semibold" />
                  </Field>
                  <div className="flex flex-wrap justify-end gap-3">
                    <button type="button" className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold" onClick={() => resetForm()}>
                      Reset
                    </button>
                    <button
                      type="submit"
                      disabled={saving || loading || loadingWorkspace}
                      className="business-center-os-button business-center-os-button--primary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold disabled:opacity-60"
                    >
                      {saving ? "Saving..." : "Create Task"}
                    </button>
                  </div>
                </form>
              ) : null}

              <div className="mt-6">
                {viewMode === "list" ? (
                  <div className="overflow-hidden rounded-3xl border border-[rgba(6,182,212,.16)]">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-[rgba(6,182,212,.12)] text-left">
                        <thead className="bg-[rgba(255,255,255,.05)] text-xs font-black uppercase tracking-[0.14em] text-[#06B6D4]">
                          <tr>
                            <Th>Task</Th>
                            <Th>Client / Project</Th>
                            <Th>Status</Th>
                            <Th>Priority</Th>
                            <Th>Due</Th>
                            <Th>Assigned</Th>
                            <Th>Timestamps</Th>
                            <Th>Actions</Th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[rgba(6,182,212,.1)] bg-[rgba(255,255,255,.03)]">
                          {filteredTasks.map((task) => (
                            <tr key={task.id} className="transition hover:bg-[rgba(255,255,255,.06)]">
                              <Td className="font-black text-[#F8FAFC]">{task.taskTitle}</Td>
                              <Td>
                                <div className="grid gap-1">
                                  <span className="font-semibold text-[#F8FAFC]">{task.clientName}</span>
                                  <span className="text-xs font-semibold text-[#CBD5E1]">{task.projectName}</span>
                                </div>
                              </Td>
                              <Td><StatusPill status={task.status} /></Td>
                              <Td><PriorityPill priority={task.priority} /></Td>
                              <Td className="text-[#E2E8F0]">{task.dueDate ? formatDate(task.dueDate) : "Not set"}</Td>
                              <Td className="text-[#E2E8F0]">{task.assignedMember}</Td>
                              <Td>
                                <div className="grid gap-1 text-xs font-semibold text-[#CBD5E1]">
                                  <span>Created {formatDate(task.createdAt)}</span>
                                  <span>Updated {formatDate(task.updatedAt)}</span>
                                </div>
                              </Td>
                              <Td>
                                <div className="flex flex-wrap gap-2">
                                  <button type="button" className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-10 items-center rounded-full px-3 text-xs font-extrabold" onClick={() => setSelectedTaskId(task.id)}>
                                    View
                                  </button>
                                  <button type="button" className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-10 items-center rounded-full px-3 text-xs font-extrabold" onClick={() => handleDeleteTask(task.id)}>
                                    <Trash2 className="size-3" aria-hidden="true" />
                                    Delete
                                  </button>
                                </div>
                              </Td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="grid gap-4 xl:grid-cols-5">
                    {kanbanColumns.map((column) => (
                      <section key={column.status} className="rounded-3xl border border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.03)] p-4">
                        <div className="flex items-center justify-between gap-3 border-b border-[rgba(6,182,212,.12)] pb-3">
                          <div>
                            <p className="text-xs font-black uppercase tracking-[0.16em] text-[#06B6D4]">{column.status}</p>
                            <h3 className="mt-1 font-display text-2xl font-bold text-[#F8FAFC]">{column.tasks.length}</h3>
                          </div>
                          <StatusPill status={column.status as BusinessCenterTaskRecord["status"]} />
                        </div>
                        <div className="mt-4 grid gap-3">
                          {column.tasks.length ? (
                            column.tasks.map((task) => (
                              <button
                                key={task.id}
                                type="button"
                                onClick={() => setSelectedTaskId(task.id)}
                                className="text-left rounded-2xl border border-[rgba(6,182,212,.12)] bg-[rgba(7,26,47,.7)] p-4 transition hover:-translate-y-0.5 hover:border-[rgba(6,182,212,.26)]"
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div>
                                    <p className="font-display text-xl font-bold text-[#F8FAFC]">{task.taskTitle}</p>
                                    <p className="mt-1 text-xs font-black uppercase tracking-[0.14em] text-[#06B6D4]">{task.taskNumber}</p>
                                  </div>
                                  <PriorityPill priority={task.priority} />
                                </div>
                                <p className="mt-3 text-sm font-semibold leading-7 text-[#E2E8F0]">{task.description}</p>
                                <p className="mt-3 text-xs font-semibold uppercase tracking-[0.14em] text-[#CBD5E1]">
                                  {task.clientName} / {task.projectName}
                                </p>
                                <p className="mt-2 text-xs font-semibold text-[#CBD5E1]">
                                  Due {task.dueDate ? formatDate(task.dueDate) : "Not set"}
                                </p>
                              </button>
                            ))
                          ) : (
                            <div className="rounded-2xl border border-dashed border-[rgba(6,182,212,.18)] bg-[rgba(255,255,255,.02)] p-4">
                              <p className="text-sm font-semibold leading-7 text-[#CBD5E1]">No tasks in this column.</p>
                            </div>
                          )}
                        </div>
                      </section>
                    ))}
                  </div>
                )}
              </div>
            </article>

              <aside className="grid min-w-0 gap-6">
              <section className="business-center-os-card business-center-os-card--light min-w-0 overflow-hidden rounded-3xl p-5 sm:p-6">
                <div className="border-b border-[rgba(31,41,55,.12)] pb-4">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-[#14B8A6]">Task Detail</p>
                  <h3 className="mt-1 font-display text-3xl font-bold text-[#1F2937]">Current task</h3>
                </div>
                {selectedTask ? (
                  <div className="mt-5 grid gap-4">
                    <div className="rounded-3xl border border-[rgba(20,184,166,.16)] bg-white p-4">
                      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#14B8A6]">{selectedTask.taskNumber}</p>
                      <h4 className="mt-2 font-display text-3xl font-bold text-[#1F2937]">{selectedTask.taskTitle}</h4>
                      <p className="mt-2 text-sm font-semibold leading-7 text-[#475569]">{selectedTask.description}</p>
                    </div>
                    <InfoRow label="Client" value={selectedTask.clientName} />
                    <InfoRow label="Project" value={selectedTask.projectName} />
                    <InfoRow label="Assigned member" value={selectedTask.assignedMember} />
                    <InfoRow label="Due date" value={selectedTask.dueDate ? formatDate(selectedTask.dueDate) : "Not set"} />
                    <InfoRow label="Created" value={formatDate(selectedTask.createdAt)} />
                    <InfoRow label="Updated" value={formatDate(selectedTask.updatedAt)} />
                    <div className="flex flex-wrap gap-2">
                      {taskStatusOptions.map((status) => (
                        <button key={status} type="button" onClick={() => handleTaskStatusChange(selectedTask.id, status)} className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-10 items-center rounded-full px-3 text-xs font-extrabold">
                          {status}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteTask(selectedTask.id)}
                      className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-full px-4 text-sm font-extrabold"
                    >
                      <Trash2 className="size-4" aria-hidden="true" />
                      Remove task
                    </button>
                  </div>
                ) : (
                  <div className="mt-5 rounded-3xl border border-dashed border-[rgba(20,184,166,.16)] bg-[rgba(20,184,166,.06)] p-4">
                    <p className="text-sm font-semibold leading-7 text-[#475569]">Select a task to inspect it here.</p>
                  </div>
                )}
              </section>

              <section className="business-center-os-card business-center-os-card--dark min-w-0 overflow-hidden rounded-3xl p-5 sm:p-6">
                <div className="border-b border-[rgba(6,182,212,.16)] pb-4">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Module Notes</p>
                  <h3 className="mt-1 font-display text-3xl font-bold text-[#F8FAFC]">Foundation state</h3>
                </div>
                <div className="mt-5 grid gap-3">
                  <NoteCard title="Brand separation" note="Tasks are stored per brand and do not alter Memory Media Vault behavior." />
                  <NoteCard title="Safe persistence" note="Task state is local-first for now, which avoids risking live business records." />
                  <NoteCard title="Next extension point" note="This module can later connect to a shared schema and workflow engine." />
                </div>
              </section>
            </aside>
          </section>
        </div>
      </section>
      <UniversalOperationalRecordDrawer
        open={recordDrawerOpen}
        tickler={selectedTickler}
        state={operationalState}
        persistenceStatus={operationalStatus}
        relatedRoute={selectedTickler ? getRelatedRecordRoute(selectedTickler) : ""}
        onClose={() => setRecordDrawerOpen(false)}
        onStatusChange={handleUniversalTicklerStatus}
        onAddNote={handleRecordDetailNote}
        onAddActivity={handleRecordDetailActivity}
      />
    </BusinessCenterOSAdminShell>
  );
}

function StatusPill({ status }: { status: BusinessCenterTaskRecord["status"] }) {
  const tone =
    status === "Complete"
      ? "bg-emerald-500/18 text-emerald-200 border-emerald-400/30"
      : status === "In Progress"
        ? "bg-sky-500/18 text-sky-200 border-sky-400/30"
        : status === "Waiting"
          ? "bg-[#FF6A2B]/12 text-[#FF6A2B] border-[#FF6A2B]/30"
          : status === "On Hold"
            ? "bg-orange-500/18 text-orange-200 border-orange-400/30"
            : "bg-white/8 text-[#fff9ef] border-[#FF6A2B]/18";
  return <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-black uppercase tracking-[0.14em] ${tone}`}>{status}</span>;
}

function QueueSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <label className="grid gap-2 text-sm font-extrabold text-[#F8FAFC]">
      {label}
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function UniversalQueueCard({
  tickler,
  selected,
  onSelect,
  onOpenDrawer,
  onStatusChange,
}: {
  tickler: UniversalTickler;
  selected: boolean;
  onSelect: () => void;
  onOpenDrawer: () => void;
  onStatusChange: (status: UniversalTickler["status"]) => void;
}) {
  const relatedRoute = getRelatedRecordRoute(tickler);
  return (
    <article className={`rounded-3xl border p-4 transition ${selected ? "border-[rgba(6,182,212,.44)] bg-[rgba(6,182,212,.12)]" : "border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.03)]"}`}>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <button type="button" onClick={onSelect} className="focus-ring min-w-0 flex-1 rounded-2xl text-left">
          <div className="flex flex-wrap items-center gap-2">
            <QueueBadge label={tickler.status} />
            <QueueBadge label={tickler.priority} />
            <QueueBadge label={formatQueueLabel(tickler.workspace)} />
          </div>
          <h3 className="mt-3 font-display text-2xl font-bold text-[#F8FAFC]">{tickler.reminderType}</h3>
          <p className="mt-2 text-sm font-semibold leading-7 text-[#E2E8F0]">{tickler.notes}</p>
          <div className="mt-3 grid gap-2 text-xs font-bold uppercase tracking-[0.12em] text-[#CBD5E1] md:grid-cols-2">
            <span>Related: {formatQueueLabel(tickler.recordType)} / {tickler.recordId}</span>
            <span>Assigned: {tickler.assignedUser}</span>
            <span>Due: {formatDate(tickler.dueDate)} at {tickler.dueTime}</span>
            <span>Next action: {tickler.status === "Completed" ? "Review completed activity" : tickler.notes}</span>
          </div>
        </button>
        <div className="flex shrink-0 flex-wrap gap-2 lg:max-w-56">
          <button type="button" onClick={onOpenDrawer} className="business-center-os-button business-center-os-button--primary focus-ring inline-flex min-h-10 items-center rounded-full px-3 text-xs font-extrabold">
            View Details
          </button>
          {(["Completed", "Snoozed", "Escalated"] as const).map((status) => (
            <button key={status} type="button" onClick={() => onStatusChange(status)} className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-10 items-center rounded-full px-3 text-xs font-extrabold">
              {status}
            </button>
          ))}
          {relatedRoute ? (
            <Link to={relatedRoute} className="business-center-os-button business-center-os-button--primary focus-ring inline-flex min-h-10 items-center gap-2 rounded-full px-3 text-xs font-extrabold">
              Open Related
              <ExternalLink className="size-3" aria-hidden="true" />
            </Link>
          ) : (
            <button type="button" disabled className="inline-flex min-h-10 items-center rounded-full border border-white/10 bg-white/[.04] px-3 text-xs font-extrabold text-[#CBD5E1] opacity-60">
              Related route unavailable
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

function UniversalQueueEmptyState({ queueView, assignedUser }: { queueView: QueueView; assignedUser: string }) {
  const message =
    queueView === "due-today"
      ? "No work is due today for the current filters."
      : queueView === "overdue"
        ? "No overdue work matches the current filters."
        : assignedUser !== "all"
          ? "No items are assigned to this user with the current filters."
          : "No records match the current filters.";
  return (
    <div className="rounded-3xl border border-dashed border-[rgba(6,182,212,.24)] bg-[rgba(255,255,255,.03)] p-6">
      <div className="flex items-start gap-3">
        <AlertCircle className="mt-1 size-5 text-[#06B6D4]" aria-hidden="true" />
        <div>
          <h3 className="font-display text-2xl font-bold text-[#F8FAFC]">Queue clear</h3>
          <p className="mt-2 text-sm font-semibold leading-7 text-[#CBD5E1]">{message}</p>
        </div>
      </div>
    </div>
  );
}

function QueueBadge({ label }: { label: string }) {
  return <span className="rounded-full border border-[rgba(6,182,212,.22)] bg-[rgba(6,182,212,.08)] px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-[#BAE6FD]">{label}</span>;
}

function InfoLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-[rgba(6,182,212,.12)] bg-[rgba(7,26,47,.7)] p-3">
      <p className="text-xs font-black uppercase tracking-[0.14em] text-[#06B6D4]">{label}</p>
      <p className="mt-1 break-words text-sm font-semibold leading-6 text-[#E2E8F0]">{value}</p>
    </div>
  );
}

function getRelatedRecordRoute(tickler: UniversalTickler) {
  if (tickler.workspace === "ar-recovery-os") {
    if (tickler.recordType === "ar_account") return "/admin/ar-recovery/customer-accounts";
    if (tickler.recordType === "ar_promise") return "/admin/ar-recovery/promise-to-pay";
    if (tickler.recordType === "letter_draft") return "/admin/ar-recovery/collection-letters";
    return "/admin/ar-recovery";
  }
  if (tickler.workspace === "business-center-os") return "/admin/business-center-os";
  if (tickler.workspace === "truevance-connect-os") return "/admin/truevance-business-center";
  if (tickler.workspace === "kreatethedate-os") return "/admin/forms-directory";
  if (tickler.workspace === "memory-media-vault-os") return "/admin/memory-hub/vault";
  return "";
}

function isUniversalBusinessNoteRecordType(recordType: UniversalRecordType): recordType is UniversalBusinessNote["recordType"] {
  return !["ar_account", "ar_promise", "recovery_record"].includes(recordType);
}

function formatQueueLabel(value: string) {
  return value
    .replace(/[-_]/g, " ")
    .replace(/\bos\b/gi, "OS")
    .replace(/\bar\b/gi, "AR")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function PriorityPill({ priority }: { priority: BusinessCenterTaskRecord["priority"] }) {
  const tone =
    priority === "Urgent"
      ? "bg-rose-500/18 text-rose-100 border-rose-400/30"
      : priority === "High"
        ? "bg-orange-500/18 text-orange-100 border-orange-400/30"
        : priority === "Medium"
          ? "bg-[#FF6A2B]/12 text-[#FF6A2B] border-[#FF6A2B]/30"
          : "bg-emerald-500/18 text-emerald-100 border-emerald-400/30";
  return <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-black uppercase tracking-[0.14em] ${tone}`}>{priority}</span>;
}

function Field({ label, children, dark = false }: { label: string; children: ReactNode; dark?: boolean }) {
  return (
    <label className={`grid gap-2 text-sm font-extrabold ${dark ? "text-[#F8FAFC]" : "text-[#1F2937]"}`}>
      {label}
      {children}
    </label>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-[rgba(20,184,166,.16)] bg-white p-4">
      <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">{label}</p>
      <p className="mt-2 break-words text-sm font-bold leading-6 text-[#1F2937]">{value}</p>
    </div>
  );
}

function NoteCard({ title, note }: { title: string; note: string }) {
  return (
    <div className="rounded-2xl border border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.04)] p-4">
      <p className="text-xs font-black uppercase tracking-[0.16em] text-[#06B6D4]">{title}</p>
      <p className="mt-2 text-sm font-semibold leading-7 text-[#E2E8F0]">{note}</p>
    </div>
  );
}

function Th({ children }: { children: string }) {
  return <th className="px-4 py-4 align-top">{children}</th>;
}

function Td({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <td className={`px-4 py-4 align-top text-sm font-semibold text-[#E2E8F0] ${className}`}>{children}</td>;
}

function useBusinessCenterOSBranding(title: string, description: string) {
  useEffect(() => {
    const fullTitle = `${title} | Business Center OS`;
    document.title = fullTitle;
    document.querySelector('meta[name="description"]')?.setAttribute("content", description);

    const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const previousTheme = themeMeta?.getAttribute("content") || "";
    if (themeMeta) themeMeta.setAttribute("content", "#1F2937");

    const faviconHref = buildBusinessCenterFavicon();
    const iconSelectors = ['link[rel="icon"]', 'link[rel="shortcut icon"]'] as const;
    const icons = iconSelectors.map((selector) => document.querySelector<HTMLLinkElement>(selector)).filter((link): link is HTMLLinkElement => Boolean(link));
    const previousIcons = icons.map((link) => link.getAttribute("href"));
    icons.forEach((link) => {
      link.setAttribute("href", faviconHref);
      link.setAttribute("type", "image/svg+xml");
    });

    return () => {
      if (themeMeta && previousTheme) themeMeta.setAttribute("content", previousTheme);
      icons.forEach((link, index) => {
        const previousHref = previousIcons[index];
        if (previousHref) link.setAttribute("href", previousHref);
      });
    };
  }, [description, title]);
}

function buildBusinessCenterFavicon() {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" role="img" aria-label="Business Center OS">
      <defs>
        <linearGradient id="g" x1="8" y1="8" x2="56" y2="56" gradientUnits="userSpaceOnUse">
          <stop offset="0" stop-color="#FF6B35"/>
          <stop offset=".52" stop-color="#14B8A6"/>
          <stop offset="1" stop-color="#06B6D4"/>
        </linearGradient>
      </defs>
      <rect x="4" y="4" width="56" height="56" rx="16" fill="#1F2937"/>
      <path d="M16 16h18c9 0 16 6.5 16 14.5S43 45 34 45H16z" fill="url(#g)"/>
      <path d="M33 16h15c-4 3.3-6.8 7.6-6.8 13.6 0 6.2 3.2 10.6 7.8 15.4H33c-8.8 0-16-6.4-16-14.5S24.2 16 33 16Z" fill="#F8FAFC" opacity=".1"/>
      <text x="32" y="39" text-anchor="middle" font-size="16" font-family="Arial, Helvetica, sans-serif" font-weight="800" fill="#F8FAFC">BC</text>
    </svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}
